import React from 'react';
import styled from 'styled-components';

import {
  getAccountInfoLogo,
  Validator,
  ValidatorWithInfo,
} from '@block-explorer/entities';
import {
  BaseProps,
  CaptionText,
  FlexColumn,
  FlexRow,
  formatHash,
  formatNumber,
  formatPercentage,
  HashLength,
  useMatchMedia,
  TruncateBox,
  SvgIcon,
  SMALL_PRECISION,
} from '@make-software/cspr-ui';
import { Tooltip, BodyText } from '@block-explorer/ui';

import Avatar from '../avatar/avatar';
import Cspr from '../cspr/cspr';
import CopyButton from '../copy-button/copy-button';
import { Trans, useTranslation } from 'react-i18next';
import { PrecisionCase } from '../../utils/currency';
import { isValidPublicKey } from '../../utils/account';

const StyledFlexRow = styled(FlexRow)<
  Pick<ValidatorInfoRowProps, 'variation'> & {
    isSelectedItem?: boolean;
  }
>(({ theme, variation = 'transparent', isSelectedItem }) =>
  theme.withMedia({
    width: '100%',
    padding: isSelectedItem
      ? ['7px', '8px 16px', '0 16px']
      : ['8px 16px', '8px 16px', '0 16px'],
    minHeight: [76, 76, 48],
    borderRadius: isSelectedItem ? theme.borderRadius.base : null,
    backgroundColor: {
      gray: theme.styleguideColors.fillSecondary,
    }[variation],
    border: {
      transparent: theme.border.base,
      gray: '0',
    }[variation],
  })
);

const KeyContainer = styled(FlexRow)(({ theme }) => ({
  width: '40%',
}));

const FeeContainer = styled.div(({ theme }) => ({
  textAlign: 'right',
  width: '20%',
}));

const StakeContainer = styled.div(({ theme }) => ({
  textAlign: 'right',
  width: '35%',
}));

const CustomValidatorText = styled(BodyText)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const ArrowSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentPrimary,
  },
  marginLeft: 16,
}));

/* eslint-disable-next-line */
export interface ValidatorInfoRowProps extends BaseProps {
  validator?: ValidatorWithInfo | string | null;
  isHidden?: boolean;
  isCopyEnabled?: boolean;
  showLabel?: boolean;
  label?: string;
  variation?: 'transparent' | 'gray';
  isSelectedItem?: boolean;
  disabled?: boolean;
}

type ResponsiveValidatorInfoRowProps = Omit<
  ValidatorInfoRowProps,
  'validator'
> & { validator: ValidatorWithInfo };

const ResponsiveValidatorDelegateInfoRow = React.forwardRef<
  HTMLDivElement,
  ResponsiveValidatorInfoRowProps
>(
  (
    {
      validator,
      isCopyEnabled,
      label = 'Validator',
      showLabel,
      isHidden,
      isSelectedItem,
      disabled,
      ...restProps
    }: ResponsiveValidatorInfoRowProps,
    ref
  ) => {
    const { t } = useTranslation();
    const responsiveHashSize = useMatchMedia(
      [HashLength.TINY, HashLength.TINY, HashLength.LITTLE, HashLength.LITTLE],
      []
    );
    const {
      key,
      delegatorCount,
      delegationFee,
      totalStakeAmount,
      selfStakePercentage,
      accountInfo,
      csprName,
    } = validator;

    const logo = getAccountInfoLogo(accountInfo);
    const name = accountInfo?.info?.owner?.name;
    const csprNameOrPublicKey = csprName || key;

    const onDesktop = (
      <FlexColumn>
        {showLabel && (
          <BodyText scale={'sm'} size={1}>
            {label}
          </BodyText>
        )}
        <StyledFlexRow
          ref={ref}
          align="center"
          itemsSpacing={12}
          {...restProps}
        >
          <KeyContainer align="center" itemsSpacing={10}>
            {logo ? (
              <Avatar src={logo} size="small" />
            ) : (
              <Avatar hash={key} size="small" />
            )}
            <Tooltip title={csprNameOrPublicKey}>
              <FlexColumn>
                {name ? (
                  <>
                    <FlexRow itemsSpacing={4} align={'baseline'}>
                      <BodyText size={3} monotype={!csprName}>
                        {isValidPublicKey(csprNameOrPublicKey)
                          ? formatHash(key, responsiveHashSize)
                          : csprName}
                      </BodyText>
                      {isCopyEnabled && (
                        <CopyButton value={csprNameOrPublicKey} />
                      )}
                    </FlexRow>
                    <CaptionText size={2} variation="darkGray">
                      {name}
                    </CaptionText>
                  </>
                ) : (
                  <FlexRow itemsSpacing={4} align={'baseline'}>
                    <BodyText size={3} monotype>
                      {isValidPublicKey(csprNameOrPublicKey)
                        ? formatHash(key, responsiveHashSize)
                        : csprName}
                    </BodyText>
                    {isCopyEnabled && (
                      <CopyButton value={csprNameOrPublicKey} />
                    )}
                  </FlexRow>
                )}
              </FlexColumn>
            </Tooltip>
          </KeyContainer>
          <FeeContainer>
            <FlexColumn>
              <BodyText variation="darkGray" size={3}>
                {formatPercentage(delegationFee)}%
              </BodyText>
              <CaptionText variation="darkGray" size={2}>
                {delegatorCount} <Trans t={t}>delegators</Trans>
              </CaptionText>
            </FlexColumn>
          </FeeContainer>
          <StakeContainer>
            <BodyText size={3} monotype noWrap>
              <Cspr
                motes={totalStakeAmount}
                precisionCase={PrecisionCase.deployCost}
              />
            </BodyText>{' '}
            <BodyText variation="darkGray" size={3}>
              (
              {formatNumber(selfStakePercentage, {
                precision: SMALL_PRECISION,
              })}
              %)
            </BodyText>
          </StakeContainer>
          {!disabled && isSelectedItem ? (
            <ArrowSvgIcon src="assets/icons/ic-arrow-down.svg" />
          ) : null}
        </StyledFlexRow>
      </FlexColumn>
    );

    const onMobile = (
      <FlexColumn>
        {showLabel && (
          <BodyText scale={'sm'} size={1}>
            {label}
          </BodyText>
        )}
        <StyledFlexRow
          ref={ref}
          justify="space-between"
          align={'center'}
          isSelectedItem={isSelectedItem}
          {...restProps}
        >
          <FlexRow
            align="center"
            justify="space-between"
            grow={2}
            itemsSpacing={12}
          >
            <FlexRow>
              <FlexColumn itemsSpacing={4}>
                <FlexRow align="center" itemsSpacing={10}>
                  {logo ? (
                    <Avatar src={logo} size="small" />
                  ) : (
                    <Avatar hash={key} size="small" />
                  )}
                  <Tooltip title={csprNameOrPublicKey}>
                    <FlexColumn>
                      {name ? (
                        <>
                          <FlexRow itemsSpacing={4}>
                            <BodyText size={3} monotype={!csprName}>
                              {isValidPublicKey(csprNameOrPublicKey)
                                ? formatHash(key, responsiveHashSize)
                                : csprName}
                            </BodyText>
                            {isCopyEnabled && (
                              <CopyButton value={csprNameOrPublicKey} />
                            )}
                          </FlexRow>
                          <CaptionText size={2} variation="darkGray">
                            <TruncateBox size={1}>{name}</TruncateBox>
                          </CaptionText>
                        </>
                      ) : (
                        <FlexRow itemsSpacing={4}>
                          <BodyText size={3} monotype={!csprName}>
                            {isValidPublicKey(csprNameOrPublicKey)
                              ? formatHash(key, responsiveHashSize)
                              : csprName}
                          </BodyText>
                          {isCopyEnabled && (
                            <CopyButton value={csprNameOrPublicKey} />
                          )}
                        </FlexRow>
                      )}
                    </FlexColumn>
                  </Tooltip>
                </FlexRow>
                <FlexRow>
                  <CaptionText variation="darkGray" size={2}>
                    {isSelectedItem && 'Fee:'} {formatPercentage(delegationFee)}
                    % {formatNumber(delegatorCount)}{' '}
                    <Trans t={t}>delegators</Trans>
                  </CaptionText>
                </FlexRow>
              </FlexColumn>
            </FlexRow>
            <FlexRow>
              <FlexColumn align="end">
                <BodyText size={3} monotype noWrap>
                  <FlexColumn align="end">
                    <FlexRow>
                      <Cspr
                        motes={totalStakeAmount}
                        precisionCase={PrecisionCase.deployCost}
                        hideCsprCurrency
                      />
                    </FlexRow>
                    <FlexRow>CSPR</FlexRow>
                  </FlexColumn>
                </BodyText>{' '}
                <BodyText variation="darkGray" size={3}>
                  ({selfStakePercentage}%)
                </BodyText>
              </FlexColumn>
            </FlexRow>
          </FlexRow>
          {!disabled && isSelectedItem ? (
            <ArrowSvgIcon src="assets/icons/ic-arrow-down.svg" />
          ) : null}
        </StyledFlexRow>
      </FlexColumn>
    );

    const responsiveValidatorDelegateInfoRow = useMatchMedia<any>(
      [onMobile, onMobile, onDesktop, onDesktop],
      [validator]
    );

    return responsiveValidatorDelegateInfoRow;
  }
);

export const ValidatorInfoRow = React.forwardRef<
  HTMLDivElement,
  ValidatorInfoRowProps
>((props: ValidatorInfoRowProps, ref) => {
  if (!props.validator || props.isHidden) {
    return null;
  }

  if (typeof props.validator === 'string') {
    return (
      <StyledFlexRow align="center">
        <CustomValidatorText size={3} monotype>
          {props.validator}
        </CustomValidatorText>
      </StyledFlexRow>
    );
  }

  return (
    <ResponsiveValidatorDelegateInfoRow
      {...props}
      validator={props.validator}
      ref={ref}
    />
  );
});

export default ValidatorInfoRow;
